import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame } from "lucide-react";

// Mobile-parity tip celebration: a spring slide-in banner + gentle confetti burst.
// Triggered on the SENDER side after a paid checkout-status poll ("Tip sent") and on the
// RECIPIENT side from the `tip.received` user-socket event ("Tip received!").

const CONFETTI_COLORS = ["#01FABD", "#3591F4", "#8B5CF6", "#F472B6", "#FACC15", "#34D399"];

const dollars = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const TipCelebrationContext = createContext({ celebrate: () => {} });
export const useTipCelebration = () => useContext(TipCelebrationContext);

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        left: Math.random() * 100,
        drift: (Math.random() * 2 - 1) * 90,
        rot: (Math.random() * 2 - 1) * 720,
        dur: 1.7 + Math.random() * 0.9,
        delay: Math.random() * 0.5,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        w: 6 + Math.random() * 5,
        h: 10 + Math.random() * 6,
        round: Math.random() > 0.6,
      })),
    [],
  );
  return (
    <div className="pointer-events-none fixed inset-0 z-[65] overflow-hidden" aria-hidden data-testid="tip-confetti">
      {pieces.map((p, i) => (
        <span
          key={i}
          className="tip-confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            width: `${p.w}px`,
            height: `${p.h}px`,
            borderRadius: p.round ? "9999px" : "2px",
            "--tc-x": `${p.drift}px`,
            "--tc-rot": `${p.rot}deg`,
            "--tc-dur": `${p.dur}s`,
            "--tc-delay": `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function Banner({ variant, amount, handle, message, title, subtitle, onDismiss }) {
  const isFounding = variant === "founding";
  const isSent = variant === "sent";
  const who = handle ? `@${handle}` : "someone";
  const bannerTitle = isFounding ? title || "You're a Founding Member!" : isSent ? "Tip sent" : "Tip received!";
  const bannerSubtitle = isFounding
    ? subtitle || "Lifetime 50% off BLOQ+"
    : isSent
      ? `You sent ${dollars(amount)} to ${who}`
      : `${who} sent you ${dollars(amount)}`;
  return (
    <>
      <Confetti />
      <motion.div
        role="status"
        data-testid="tip-celebration-banner"
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -90, opacity: 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 26 }}
        onClick={onDismiss}
        className="fixed left-1/2 top-4 z-[70] w-[min(92vw,360px)] -translate-x-1/2 cursor-pointer"
      >
        <div
          className="flex items-start gap-3 rounded-2xl border p-3.5"
          style={{
            background: "#0B1626",
            borderColor: "rgba(1,250,189,0.45)",
            boxShadow: "0 0 12px rgba(1,250,189,0.25)",
          }}
        >
          <span className="flex text-2xl leading-none" aria-hidden>
            {isFounding ? <Flame className="h-6 w-6 text-bloq-mint" /> : isSent ? "💸" : "🎁"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-bold" style={{ color: "#01FABD", fontSize: "14px" }} data-testid="tip-celebration-title">
              {bannerTitle}
            </div>
            <div
              className="mt-0.5 truncate"
              style={{ color: "#E8F0F8", fontSize: "12.5px" }}
              data-testid="tip-celebration-subtitle"
            >
              {bannerSubtitle}
            </div>
            {message ? (
              <div
                className="mt-1 line-clamp-2 italic"
                style={{ color: "#E8F0F8", fontSize: "12.5px", opacity: 0.85 }}
                data-testid="tip-celebration-message"
              >
                “{message}”
              </div>
            ) : null}
          </div>
        </div>
      </motion.div>
    </>
  );
}

export function TipCelebrationProvider({ children }) {
  const [celebration, setCelebration] = useState(null);
  const idRef = useRef(0);
  const timerRef = useRef(null);

  const celebrate = useCallback((payload) => {
    idRef.current += 1;
    setCelebration({ ...payload, key: idRef.current });
  }, []);

  useEffect(() => {
    if (!celebration) return undefined;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCelebration(null), 4500);
    return () => clearTimeout(timerRef.current);
  }, [celebration]);

  return (
    <TipCelebrationContext.Provider value={{ celebrate }}>
      {children}
      <AnimatePresence>
        {celebration ? (
          <Banner
            key={celebration.key}
            variant={celebration.variant}
            amount={celebration.amount}
            handle={celebration.handle}
            message={celebration.message}
            title={celebration.title}
            subtitle={celebration.subtitle}
            onDismiss={() => setCelebration(null)}
          />
        ) : null}
      </AnimatePresence>
    </TipCelebrationContext.Provider>
  );
}
